import { useAuth } from '@clerk/clerk-react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { RequireAuth } from './components/RequireAuth';
import { ThemeColorEffect } from './components/ThemeColorEffect';
import { UpdateBanner } from './components/UpdateBanner';
import DiagnosticsPage from './pages/DiagnosticsPage';
import HomePage from './pages/HomePage';
import InboxPage from './pages/InboxPage';
import JoinPage from './pages/JoinPage';
import MemoryPage from './pages/MemoryPage';
import MePage from './pages/MePage';
import NotFoundPage from './pages/NotFoundPage';
import OnboardingCompletePage from './pages/OnboardingCompletePage';
import SettingsPage from './pages/SettingsPage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import { readPersonalPage } from './lib/storage';
/**
 * Root route decision tree:
 *
 *   Signed in + personalPage in localStorage   → /me (steady state)
 *   Signed in + no personalPage                → /onboarding/complete (hydrate from API)
 *   Not signed in + personalPage               → /sign-up (mid-onboarding, just redeemed)
 *   Not signed in + no personalPage            → HomePage
 *
 * The signed-in-but-no-personalPage branch covers two cases:
 *   1. Fresh sign-up flow before /onboarding/complete has run
 *   2. Returning user on a new device (no localStorage)
 *
 * Both resolve at /onboarding/complete, which hydrates localStorage from
 * the API and forwards to /me. This is the canonical hydration point —
 * MePage and SettingsPage can assume personalPage exists.
 */
function RootRoute() {
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) {
    // Brief pre-Clerk state — render HomePage so first paint feels instant.
    // The next render (after Clerk loads) will redirect if appropriate.
    return <HomePage />;
  }
  const personalPage = readPersonalPage();
  if (isSignedIn) {
    return personalPage ? (
      <Navigate to="/me" replace />
    ) : (
      <Navigate to="/onboarding/complete" replace />
    );
  }
  if (personalPage) {
    return <Navigate to="/sign-up" replace />;
  }
  return <HomePage />;
}
export default function App() {
  return (
    <>
      {/*
        Phase 7a (14 Jun 2026) — global SW-update prompt. Mounted
        outside <Routes> so it persists across every navigation;
        renders nothing until a newer service worker reaches
        `installed` state with a controller already in place
        (production scenario only — not on first install). The
        banner uses fixed positioning, so it overlays the active
        route without disturbing its layout.
      */}
      <UpdateBanner />
      {/*
        Phase 7-theme (14 Jun 2026) — partner-aware OS chrome.
        Mounted outside <Routes> so the iOS Safari address bar /
        Android Chrome bar / iOS standalone status bar track
        partner.theme.primaryHex across every authed route — same
        partner-aware feel as the bubbles, compass icon, and
        avatar. Bare-town and pre-signup guests stay on the
        TownInsider default. Renders nothing — pure side-effect
        host driving the <meta name="theme-color"> tag.
      */}
      <ThemeColorEffect />
      <Routes>
        <Route path="/" element={<RootRoute />} />
      <Route path="/join/:townSlug" element={<JoinPage />} />
      {/*
        Sign-up and sign-in routes use path-based routing (Clerk's
        recommended pattern for React Router). Trailing /* lets Clerk
        handle its own sub-routes (verification, factor-two, etc.)
        beneath /sign-up and /sign-in.
      */}
      <Route path="/sign-up/*" element={<SignUpPage />} />
      <Route path="/sign-in/*" element={<SignInPage />} />
      <Route
        path="/onboarding/complete"
        element={<OnboardingCompletePage />}
      />
      <Route
        path="/me"
        element={
          <RequireAuth>
            <MePage />
          </RequireAuth>
        }
      />
      <Route
        path="/inbox"
        element={
          <RequireAuth>
            <InboxPage />
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth>
            <SettingsPage />
          </RequireAuth>
        }
      />
      <Route path="/memories/:token" element={<MemoryPage />} />
      {/*
        Diagnostics — Phase 6 sub-commit 8 (13 Jun 2026). Hidden
        support route, not linked from anywhere in the app chrome.
        Deliberately NOT auth-gated: the most common reason a guest
        would visit this URL is that they're having sign-in / sync
        problems, in which case RequireAuth would defeat the purpose.
        The page only exposes the guest's own device state (their
        userId from useAuth, their outbox, their install-nudge
        dismissal, their browser/SW state) so there's no cross-user
        leak — a signed-out visitor sees `userId: null`, which is
        itself a diagnostic signal rather than a leak.
      */}
      <Route path="/diagnostics" element={<DiagnosticsPage />} />
      <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
